/**
 * Localized copy for BFF error codes (`FlowIssue.code`). The wire contract is
 * code-based; prose here is portal-side so every locale controls its own
 * wording. `emailChallenge*` variants are refined from the BFF's message text
 * (same code, different causes) in `i18n/errors.ts`.
 */

export const errors = {
  validationError: 'The request was not valid. Check the form and try again.',
  unauthorized: 'Your session has expired. Please sign in again.',
  forbidden: "You don't have access to this. Contact your administrator if that seems wrong.",
  csrfFailed: 'The request could not be verified. Reload the page and try again.',
  notFound: 'Not found — it may have been removed or expired.',
  payloadTooLarge: 'The attachments are too large. Remove some files and try again.',
  conflict: 'This run has moved on — refresh to see its current state.',
  rateLimited: 'Too many requests. Wait a moment and try again.',
  webauthnFailed: 'Passkey verification failed. Please try again.',
  emailChallengeGeneric: 'Invalid or expired code.',
  emailChallengeIncorrect: 'Incorrect code. Check the email and try again.',
  emailChallengeExpired: 'This code has expired. Request a new one.',
  emailChallengeUsed: 'This code was already used. Request a new one.',
  emailChallengeTooMany: 'Too many attempts. Request a new code.',
  notProvisioned: 'Your account has no portal access yet. Ask your administrator for an invitation.',
  accessForbidden: 'Only an administrator of this company can manage access.',
  accessStepUpRequired:
    'You signed in with an e-mail code. To manage access, sign out and sign in again, and create a passkey right after the code (or use the one you have).',
  memberNotFound: 'That person is no longer in this company. Reload the page.',
  lastAdmin: 'The company would be left without an administrator. Make someone else an administrator first.',
  accessSelfChange: "You can't change your own access. Ask another administrator.",
  inviteDomainNotAllowed:
    'That address is outside the e-mail domains your company allows. Check the address or ask EVT to add the domain.',
  passkeyResetNotAllowed:
    'This person also belongs to another company, so only an EVT platform administrator can reset their passkeys.',
  tenantNotFound: 'That company does not exist.',
  tenantExists: 'A company with that id already exists.',
  upstreamFailed: 'The service is having trouble right now. Please try again shortly.',
  upstreamMisconfigured: 'The service is misconfigured. Our team has been notified.',
  executionInFlight: 'This run is still being processed. Try again in a moment.',
  memoryNotFound: 'That memory is no longer there — it may have been removed already.',
  memoryNotYours: 'That memory belongs to your company, so only your administrator can change it.',
  memoryConflict: 'This memory changed while you had it open. Reload the page and try again.',
  memoryFull:
    "There is no room left in this Smith's memory. Stop using one of your memories, or ask your administrator for room.",
  memoryUnavailable: 'Memories are unavailable right now. Please try again shortly.',
  memoryNotConfigured: 'Memories are not available for your account yet.',
  memoryOff: 'This Smith is no longer keeping memories. Reload the page to see the ones that are.',
  memoryTooLong: 'What you wrote is longer than a memory can be. Shorten it and save again.',
  memoryTitleRequired: 'Write your memory as one sentence in the first field, then save again.',
  requesterUnresolved: "We couldn't confirm who you are just now. Please try again in a moment.",
  unexpected: 'Something unexpected went wrong. Please try again.',
  feedbackLinkInvalid: 'This rating link is invalid or has expired.',
  feedbackUnavailable: 'Rating is unavailable right now. Try again in a few minutes.',
  flowNotAvailable: 'This flow is not available to your company. Pick one from Flows.',
  flowNotOffered:
    'A flow you chose is no longer offered to this company. Reload the page to see the flows it can have.',
  flowAccessConflict:
    "Someone else changed this company's flows while you had them open. Reload to see their change, then choose again.",

  // Files (uploads in parts, fr-docs attachments by reference, deliverables by reference). Never the server's own
  // words: those name hosts, sizes and internal stages written for an operator.
  attachmentTooLarge: 'The files are larger than one request can carry. Remove some, or send fewer at a time.',
  attachmentFetchFailed: "We couldn't read one of your files. Attach it again and try once more.",
  attachmentIntegrityFailed:
    'One of your files did not arrive intact. Attach it again and try once more — the original on your device is fine.',
  attachmentSourceForbidden:
    "One of your files couldn't be handed to the service. This is a setup problem on our side; please contact support.",
  sandboxStagingFailed:
    "Your files couldn't be prepared for the work, so nothing was done with them. Please try again in a few minutes.",
  deliverableUnavailable:
    'One of the files this run produced could not be retrieved, so it is missing from this delivery. Ask for it again, or run the request again.',
  uploadNotFound: 'This file is no longer available. Remove it and attach it again.',
  uploadExpired:
    'One of your files was uploaded too long ago and is no longer available. Remove it, attach it again, and send once more.',
  uploadNotReady: 'A file is still uploading. Wait for it to finish, then send again.',
  uploadPartInvalid: 'Part of a file did not arrive correctly. Remove the file and attach it again.',
  uploadIncomplete: 'A file did not finish uploading. Retry it, or remove it and attach it again.',
  uploadBusy: 'Many files are being uploaded right now. Yours will continue in a moment.',
  resultPending: 'The delivery is still being prepared. It will appear here in a moment.',
  resultNotFound: 'The files of this run are not available here.'
}
